import { useState, useEffect, useCallback } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CourtGridTimeSlot } from "../../../models/CourtGridData";

export default function useCourtUI(exerciseName?: string) {
  const isMobile = useMediaQuery("(max-width: 980px)");

  const [internalSelectedDate, setInternalSelectedDate] = useState<Date>(new Date());
  const [internalSelectedCourts, setInternalSelectedCourts] = useState<string[]>([]);
  const [persistentSelectedCourts, setPersistentSelectedCourts] = useState<string[]>([]);
  const [showPastHours, setShowPastHours] = useState(false);
  const [currentCourtIndex, setCurrentCourtIndex] = useState(0);
  const [reservationModal, setReservationModal] = useState<{
    isOpen: boolean;
    exerciseId: number | null;
  }>({
    isOpen: false,
    exerciseId: null,
  });

  const handleSlotClick = useCallback((timeSlot: CourtGridTimeSlot, courtName: string) => {
    setReservationModal({
      isOpen: true,
      exerciseId: timeSlot.exerciseId,
    });
  }, []);

  const handleCloseReservationModal = useCallback(() => {
    setReservationModal({
      isOpen: false,
      exerciseId: null,
    });
  }, []);

  const handlePreviousCourt = useCallback(() => {
    setCurrentCourtIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextCourt = useCallback((maxIndex: number) => {
    setCurrentCourtIndex((prev) => Math.min(maxIndex - 1, prev + 1));
  }, []);

  const handleSetCourtIndex = useCallback((index: number) => {
    setCurrentCourtIndex(index);
  }, []);

  // Reset current court index when courts change
  useEffect(() => {
    setCurrentCourtIndex(0);
  }, [internalSelectedCourts]);

  // Reset show past hours when date changes
  useEffect(() => {
    setShowPastHours(false);
  }, [internalSelectedDate]);

  const updateCourtFilters = useCallback((selectedCourts: string[], availableCourts: string[]) => {
    // Ustaw bieżące zaznaczone korty
    setInternalSelectedCourts(selectedCourts);

    // Zapisz zaznaczone korty do persystentnego stanu
    setPersistentSelectedCourts((prev) => {
      if (selectedCourts.length === 0) {
        // Jeśli użytkownik wyczyścił wszystkie zaznaczenia, zwróć pustą tablicę
        return [];
      } else {
        // W przeciwnym razie połącz poprzednie zaznaczenia z nowymi
        const combined = [...prev, ...selectedCourts];
        // Usuń duplikaty i zwróć tylko unikalne wartości
        return Array.from(new Set(combined));
      }
    });
  }, []);

  return {
    isMobile,
    selectedDate: internalSelectedDate,
    setSelectedDate: setInternalSelectedDate,
    selectedCourts: internalSelectedCourts,
    setSelectedCourts: setInternalSelectedCourts,
    persistentSelectedCourts,
    setPersistentSelectedCourts,
    updateCourtFilters,
    showPastHours,
    setShowPastHours,
    currentCourtIndex,
    reservationModal,
    handleSlotClick,
    handleCloseReservationModal,
    handlePreviousCourt,
    handleNextCourt,
    handleSetCourtIndex,
  };
}
